import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRightLeft,
  Copy,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import { API_URL } from '../../config';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

function formatSlots(used, limit) {
  if (limit == null) return `${used}`;
  if (limit === -1) return `${used} / unlimited`;
  return `${used} / ${limit}`;
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Super-admin: list org spaces, transfer/duplicate to another org, view audit log.
 */
export default function PlatformOrgSpacesPanel({ organizationId, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [ops, setOps] = useState([]);
  const [opsLoading, setOpsLoading] = useState(false);

  const [dialog, setDialog] = useState(null); // { mode: 'transfer'|'duplicate', space }
  const [targetQ, setTargetQ] = useState('');
  const [targetResults, setTargetResults] = useState([]);
  const [targetSearchLoading, setTargetSearchLoading] = useState(false);
  const [targetOrg, setTargetOrg] = useState(null);
  const [targetCapacity, setTargetCapacity] = useState(null);
  const [note, setNote] = useState('');
  const [dupName, setDupName] = useState('');
  const [clearDomain, setClearDomain] = useState(false);
  const [unpublish, setUnpublish] = useState(false);
  const [includeDocs, setIncludeDocs] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadSpaces = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        `${API_URL}/platform/organizations/${organizationId}/spaces`
      );
      setData(res);
    } catch {
      toast.error('Failed to load spaces');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const loadOps = useCallback(async () => {
    if (!organizationId) return;
    setOpsLoading(true);
    try {
      const { data: res } = await axios.get(`${API_URL}/platform/space-operations`, {
        params: { organization_id: organizationId, limit: 30 },
      });
      setOps(res.items || []);
    } catch {
      setOps([]);
    } finally {
      setOpsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadSpaces();
    loadOps();
  }, [loadSpaces, loadOps]);

  useEffect(() => {
    if (!dialog) return undefined;
    const q = targetQ.trim();
    if (q.length < 1) {
      setTargetResults([]);
      return undefined;
    }
    const t = setTimeout(() => {
      setTargetSearchLoading(true);
      axios
        .get(`${API_URL}/platform/organizations`, { params: { q, limit: 15 } })
        .then((res) => {
          const items = (res.data.items || []).filter((o) => o.id !== organizationId);
          setTargetResults(items);
        })
        .catch(() => setTargetResults([]))
        .finally(() => setTargetSearchLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [targetQ, dialog, organizationId]);

  const openDialog = (mode, space) => {
    setDialog({ mode, space });
    setTargetQ('');
    setTargetResults([]);
    setTargetOrg(null);
    setTargetCapacity(null);
    setNote('');
    setDupName(mode === 'duplicate' ? `Copy of ${space.name}` : '');
    setClearDomain(false);
    setUnpublish(false);
    setIncludeDocs(true);
  };

  const selectTarget = async (org) => {
    setTargetOrg(org);
    setTargetQ(org.name);
    setTargetResults([]);
    try {
      const { data: detail } = await axios.get(
        `${API_URL}/platform/organizations/${org.id}`
      );
      setTargetCapacity({
        used: detail.space_count,
        limit: detail.space_limit,
        canAdd: detail.can_add_space,
        plan: detail.subscription?.plan_name || detail.subscription?.plan_id,
      });
    } catch {
      setTargetCapacity(null);
    }
  };

  const submit = async () => {
    if (!dialog || !targetOrg) {
      toast.error('Select a target organization');
      return;
    }
    if (targetCapacity && targetCapacity.canAdd === false) {
      toast.error('Target organization has no available space slots');
      return;
    }
    setBusy(true);
    try {
      if (dialog.mode === 'transfer') {
        const { data: res } = await axios.post(
          `${API_URL}/platform/spaces/${dialog.space.id}/transfer`,
          {
            target_organization_id: targetOrg.id,
            clear_space_access: true,
            clear_custom_domain: clearDomain,
            unpublish,
            note: note.trim() || undefined,
          }
        );
        toast.success(`Transferred “${res.space_name}”`);
        (res.warnings || []).forEach((w) => toast.warning(w));
      } else {
        const { data: res } = await axios.post(
          `${API_URL}/platform/spaces/${dialog.space.id}/duplicate`,
          {
            target_organization_id: targetOrg.id,
            name: dupName.trim() || undefined,
            include_documents: includeDocs,
            include_publish_settings: true,
            include_embed_settings: true,
            publish: false,
            note: note.trim() || undefined,
          }
        );
        toast.success(`Duplicated as “${res.space_name}” → ${res.new_space_id?.slice(0, 8)}…`);
        (res.warnings || []).forEach((w) => toast.warning(w));
      }
      setDialog(null);
      await loadSpaces();
      await loadOps();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operation failed');
    } finally {
      setBusy(false);
    }
  };

  if (!organizationId) return null;

  return (
    <>
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="font-[Manrope] text-base">Spaces</CardTitle>
            <CardDescription>
              Transfer or duplicate spaces to another workspace. Target must have a free space slot
              on their plan.
              {data
                ? ` · This org: ${formatSlots(data.space_count, data.space_limit)}`
                : null}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadSpaces(); loadOps(); }} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && !data ? (
            <div className="flex items-center gap-2 text-slate-500 py-6">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading spaces…
            </div>
          ) : !data?.spaces?.length ? (
            <p className="text-sm text-slate-500 py-4 text-center">No spaces in this organization.</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded-lg border border-slate-200 dark:border-slate-800">
              {data.spaces.map((s) => (
                <li
                  key={s.id}
                  className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 justify-between"
                  data-testid={`platform-space-row-${s.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{s.name}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                      <span>{s.document_count} docs</span>
                      {s.is_published ? (
                        <Badge className="bg-emerald-600 text-[10px] h-5">Published</Badge>
                      ) : (
                        <span>Draft</span>
                      )}
                      {s.has_custom_domain ? <span>Custom domain</span> : null}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      onClick={() => openDialog('transfer', s)}
                      data-testid="platform-space-transfer"
                    >
                      <ArrowRightLeft className="w-3.5 h-3.5" />
                      Transfer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      onClick={() => openDialog('duplicate', s)}
                      data-testid="platform-space-duplicate"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      Duplicate
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-2">
            <h4 className="text-sm font-medium mb-2">Transfer / duplicate log</h4>
            {opsLoading ? (
              <p className="text-xs text-slate-500">Loading…</p>
            ) : ops.length === 0 ? (
              <p className="text-xs text-slate-500">No transfer or duplicate operations yet for this org.</p>
            ) : (
              <ul className="space-y-2 max-h-64 overflow-auto text-xs">
                {ops.map((op) => {
                  const after = op.after_state || {};
                  const before = op.before_state || {};
                  return (
                    <li
                      key={op.id}
                      className="rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2"
                    >
                      <div className="flex justify-between gap-2">
                        <span className="font-medium capitalize">
                          {(after.operation || op.action || '').replace('space_', '')}
                        </span>
                        <span className="text-slate-500">{formatWhen(op.created_at)}</span>
                      </div>
                      <p className="text-slate-600 dark:text-slate-400 mt-1">
                        <strong>{before.space_name || op.entity_id}</strong>
                        {before.from_organization_name
                          ? ` · from ${before.from_organization_name}`
                          : null}
                        {after.to_organization_name
                          ? ` → ${after.to_organization_name}`
                          : null}
                      </p>
                      <p className="text-slate-500 mt-0.5">
                        by {op.admin_email || after.admin_email || op.admin_user_id}
                        {after.note ? ` · “${after.note}”` : ''}
                        {after.new_space_id
                          ? ` · new space ${String(after.new_space_id).slice(0, 8)}…`
                          : ''}
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={Boolean(dialog)} onOpenChange={(open) => !busy && !open && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="platform-space-op-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === 'transfer' ? 'Transfer space' : 'Duplicate space'}
            </DialogTitle>
            <DialogDescription>
              {dialog?.mode === 'transfer'
                ? `Move “${dialog?.space?.name}” to another organization. Source loses the space; target must have a free slot.`
                : `Create a copy of “${dialog?.space?.name}” in another organization. Target must have a free slot.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {dialog?.mode === 'duplicate' ? (
              <div className="space-y-2">
                <Label htmlFor="dup-name">New space name</Label>
                <Input
                  id="dup-name"
                  value={dupName}
                  onChange={(e) => setDupName(e.target.value)}
                />
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="target-org">Target organization</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="target-org"
                  className="pl-9"
                  placeholder="Search by name, slug, or id…"
                  value={targetQ}
                  onChange={(e) => {
                    setTargetQ(e.target.value);
                    setTargetOrg(null);
                    setTargetCapacity(null);
                  }}
                />
              </div>
              {targetSearchLoading ? (
                <p className="text-xs text-slate-500">Searching…</p>
              ) : targetResults.length > 0 && !targetOrg ? (
                <ul className="border rounded-md max-h-40 overflow-auto text-sm">
                  {targetResults.map((o) => (
                    <li key={o.id}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800"
                        onClick={() => selectTarget(o)}
                      >
                        <div className="font-medium">{o.name}</div>
                        <div className="text-xs text-slate-500">
                          {o.slug} · {o.plan_id || '—'} · {o.space_count ?? '?'} spaces
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {targetOrg && targetCapacity ? (
                <div
                  className={`text-xs rounded-md px-3 py-2 border ${
                    targetCapacity.canAdd
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-100'
                      : 'border-red-200 bg-red-50 text-red-900 dark:bg-red-950/30 dark:border-red-800 dark:text-red-100'
                  }`}
                >
                  Target: <strong>{targetOrg.name}</strong> ({targetCapacity.plan || '—'}) · spaces{' '}
                  {formatSlots(targetCapacity.used, targetCapacity.limit)}
                  {targetCapacity.canAdd
                    ? ' · can accept a space'
                    : ' · no free slot — upgrade plan or free a space first'}
                </div>
              ) : null}
            </div>

            {dialog?.mode === 'transfer' ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="clear-domain">Remove custom domain</Label>
                  <Switch
                    id="clear-domain"
                    checked={clearDomain}
                    onCheckedChange={setClearDomain}
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="unpublish">Unpublish after transfer</Label>
                  <Switch id="unpublish" checked={unpublish} onCheckedChange={setUnpublish} />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="include-docs">Copy documents (nested tree)</Label>
                <Switch
                  id="include-docs"
                  checked={includeDocs}
                  onCheckedChange={setIncludeDocs}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="op-note">Note (logged)</Label>
              <Textarea
                id="op-note"
                rows={2}
                placeholder="Reason for this operation…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button
              disabled={busy || !targetOrg || targetCapacity?.canAdd === false}
              onClick={submit}
              data-testid="platform-space-op-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {dialog?.mode === 'transfer' ? 'Transfer' : 'Duplicate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
